// src/components/leitner/PngExportModal.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  X, Download, Image as ImageIcon, Loader2, Search, CheckSquare, Square, ChevronDown, ChevronUp
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { MathRenderer } from '../math/MathRenderer';
import CategoryFilter from './CategoryFilter';
import QuestionMediaDisplay from './QuestionMediaDisplay';

const PngExportModal = ({
  isOpen,
  onClose,
  questions,
  bookData
}) => {
  const [filters, setFilters] = useState({
    subject: null,
    grades: [],
    topics: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [previewBase64, setPreviewBase64] = useState(null);
  const [previewFilename, setPreviewFilename] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [autoPreview, setAutoPreview] = useState(false);

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setFilters({ subject: null, grades: [], topics: [] });
    setSearchQuery('');
    setSelectedQuestionId(null);
    setPreviewBase64(null);
    setPreviewFilename(null);
    setIsGenerating(false);
    setError(null);
    setExpandedQuestions(new Set());
    setAutoPreview(false);
  }, [isOpen]);

  const exportableQuestions = useMemo(() => {
    const result = [];
    questions.forEach((q) => {
      if (q.type === 'stem' && Array.isArray(q.subQuestions) && q.subQuestions.length > 0) {
        q.subQuestions.forEach((sq, idx) => {
          result.push({
            ...sq,
            id: sq.id || `${q.id}_sub_${idx}`,
            text: sq.text || sq.questionText,
            parentStemText: q.stemText,
            parentStemImage: q.stemImage,
            parentStemMedia: q.media,
            subject: sq.subject || q.subject,
            grades: sq.grades || q.grades,
            grade: sq.grade || q.grade,
            topics: sq.topics || q.topics,
            level: sq.level || q.level,
            marks: sq.marks || q.marks
          });
        });
      } else {
        result.push({
          ...q,
          text: q.text || q.questionText
        });
      }
    });
    return result;
  }, [questions]);

  const categories = useMemo(() => {
    const subjects = new Set();
    const grades = new Set();
    const topics = new Set();

    exportableQuestions.forEach(q => {
      if (q.subject) subjects.add(q.subject);
      if (Array.isArray(q.grades)) {
        q.grades.forEach(g => grades.add(String(g)));
      } else if (q.grade) {
        grades.add(String(q.grade));
      }
      if (q.topics?.length) q.topics.forEach(t => topics.add(t));
    });

    return {
      subjects: Array.from(subjects).sort(),
      grades: Array.from(grades).sort(),
      topics: Array.from(topics).sort()
    };
  }, [exportableQuestions]);

  const filteredQuestions = useMemo(() => {
    return exportableQuestions
      .filter(q => {
        if (filters.subject && q.subject !== filters.subject) return false;
        if (filters.grades && filters.grades.length > 0) {
          const questionGrades = Array.isArray(q.grades)
            ? q.grades.map(String)
            : (q.grade ? [String(q.grade)] : []);
          const hasMatchingGrade = filters.grades.some(g => questionGrades.includes(g));
          if (!hasMatchingGrade) return false;
        }
        if (filters.topics && filters.topics.length > 0) {
          const questionTopics = q.topics || [];
          const hasMatchingTopic = filters.topics.some(t => questionTopics.includes(t));
          if (!hasMatchingTopic) return false;
        }
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const text = q.type === 'simple' ? q.text : q.stemText;
          if (!text?.toLowerCase().includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || new Date(b.createdAt || 0).getTime();
        return aTime - bTime;
      });
  }, [exportableQuestions, filters, searchQuery]);

  const selectedQuestion = useMemo(
    () => filteredQuestions.find(q => q.id === selectedQuestionId) || null,
    [filteredQuestions, selectedQuestionId]
  );

  const toggleExpanded = (id) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isRenderableImageUrl = (value) => (
    typeof value === 'string' &&
    /^(https?:\/\/|data:image\/|blob:)/i.test(value.trim())
  );

  const hasRenderableMedia = (media) => (
    !!media &&
    media.type &&
    media.type !== 'none' &&
    (
      media.type !== 'images' ||
      (Array.isArray(media.images) && media.images.length > 0)
    )
  );

  const getQuestionImage = (q) => {
    if (hasRenderableMedia(q?.media)) return q.media;
    if (hasRenderableMedia(q?.stemMedia)) return q.stemMedia;
    if (isRenderableImageUrl(q?.image)) return q.image;
    if (isRenderableImageUrl(q?.stemImage)) return q.stemImage;
    return null;
  };

  const getStemMedia = (q) => {
    if (hasRenderableMedia(q?.parentStemMedia)) {
      return q.parentStemMedia;
    }
    if (isRenderableImageUrl(q?.parentStemImage)) return { type: 'images', images: [q.parentStemImage] };
    if (hasRenderableMedia(q?.stemMedia)) return q.stemMedia;
    if (isRenderableImageUrl(q?.stemImage)) return { type: 'images', images: [q.stemImage] };
    return null;
  };

  const hasActiveFilters = filters.subject || filters.grades.length > 0 || filters.topics.length > 0 || searchQuery;

  const clearFilters = () => {
    setFilters({ subject: null, grades: [], topics: [] });
    setSearchQuery('');
  };

  const generatePreview = async (question) => {
    if (!question) return;
    setIsGenerating(true);
    setError(null);

    const requestId = ++requestIdRef.current;

    try {
      const generatePng = httpsCallable(functions, 'generateQuestionCardPng');
      const resolvedMedia = hasRenderableMedia(question.media)
        ? question.media
        : (hasRenderableMedia(question.stemMedia) ? question.stemMedia : null);
      const resolvedImage = isRenderableImageUrl(question.image)
        ? question.image
        : (isRenderableImageUrl(question.stemImage) ? question.stemImage : null);

      const exportQuestion = {
        ...question,
        text: question.text || question.questionText,
        image: resolvedImage,
        media: resolvedMedia,
        stemText: question.stemText,
        stemImage: isRenderableImageUrl(question.stemImage) ? question.stemImage : null,
        stemMedia: hasRenderableMedia(question.stemMedia) ? question.stemMedia : null,
        parentStemText: question.parentStemText,
        parentStemImage: isRenderableImageUrl(question.parentStemImage) ? question.parentStemImage : null,
        parentStemMedia: hasRenderableMedia(question.parentStemMedia) ? question.parentStemMedia : null
      };

      const result = await generatePng({
        question: exportQuestion,
        logoUrl: bookData?.logoUrl || null,
        signature: 'Created with ABC Leitner'
      });

      if (requestId !== requestIdRef.current) return;

      if (!result?.data?.png) {
        throw new Error('No PNG data received from server');
      }

      setPreviewBase64(result.data.png);
      setPreviewFilename(result.data.filename || 'question_A6.png');
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('PNG preview error:', err);
      setError(err.message || 'Failed to generate preview. Please try again.');
      setPreviewBase64(null);
      setPreviewFilename(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsGenerating(false);
      }
    }
  };

  useEffect(() => {
    if (!selectedQuestion) {
      setPreviewBase64(null);
      setPreviewFilename(null);
      return;
    }
    if (autoPreview) {
      generatePreview(selectedQuestion);
    } else {
      setPreviewBase64(null);
      setPreviewFilename(null);
    }
  }, [selectedQuestionId, autoPreview]);

  const base64ToBlob = (base64, mimeType = 'image/png') => {
    const cleanBase64 = base64.replace(/[\s\n\r]/g, '');
    const binaryString = window.atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  };

  const handleExport = async () => {
    if (!selectedQuestion) return;
    if (!previewBase64) {
      await generatePreview(selectedQuestion);
      if (!previewBase64) return;
    }
    const blob = base64ToBlob(previewBase64, 'image/png');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = previewFilename || 'question_A6.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <ImageIcon className="h-6 w-6 text-indigo-400" />
            <h2 className="text-lg font-bold text-slate-100">Export A6 PNG Card</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="p-2 hover:bg-slate-800 rounded-lg transition disabled:opacity-50 text-slate-400 hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="border border-slate-700 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-slate-800/50">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
                  <Search className="h-4 w-4 text-indigo-400" />
                  Filter Questions
                  {hasActiveFilters && (
                    <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                      Active
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-800/30 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search question text..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <CategoryFilter
                  categories={categories}
                  selectedFilters={filters}
                  onFilterChange={setFilters}
                  onClear={clearFilters}
                  questions={exportableQuestions}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-300">
                  {filteredQuestions.length} questions found
                </span>
                <button
                  onClick={() => setSelectedQuestionId(null)}
                  className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300"
                >
                  <Square className="h-4 w-4" />
                  Clear Selection
                </button>
              </div>

              <div className="border border-slate-700 rounded-lg divide-y divide-slate-700 max-h-64 overflow-y-auto bg-slate-800/50">
                {filteredQuestions.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    No questions match the selected filters
                  </div>
                ) : (
                  filteredQuestions.map((q) => {
                    const isSelected = selectedQuestionId === q.id;
                    const isExpanded = expandedQuestions.has(q.id);
                    const stemMedia = getStemMedia(q);
                    const questionMedia = getQuestionImage(q);
                    return (
                      <div
                        key={q.id}
                        className={`p-3 transition ${isSelected ? 'bg-slate-800/80' : 'hover:bg-slate-800/50'}`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            onClick={() => setSelectedQuestionId(isSelected ? null : q.id)}
                            className="mt-1 shrink-0"
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-indigo-500" />
                            ) : (
                              <Square className="h-5 w-5 text-slate-500" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm text-slate-300 line-clamp-2">
                                <MathRenderer content={q.text || q.stemText} />
                              </div>
                              <button
                                onClick={() => toggleExpanded(q.id)}
                                className="p-1 text-slate-400 hover:text-slate-200"
                                title={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </button>
                            </div>
                            {q.subject && (
                              <div className="text-xs text-slate-500 mt-1">{q.subject}</div>
                            )}
                            {isExpanded && (
                              <div className="mt-2 space-y-3">
                                {q.parentStemText && (
                                  <div className="p-2 rounded-lg border border-slate-700 bg-slate-900/40">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Stem</div>
                                    <div className="text-xs text-slate-300">
                                      <MathRenderer content={q.parentStemText} />
                                    </div>
                                  </div>
                                )}
                                {stemMedia && (
                                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-2">
                                    <QuestionMediaDisplay media={stemMedia} className="max-w-full" />
                                  </div>
                                )}
                                {questionMedia && (
                                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-2">
                                    {typeof questionMedia === 'string' ? (
                                      <img src={questionMedia} alt="Question" className="w-full h-auto rounded" />
                                    ) : (
                                      <QuestionMediaDisplay media={questionMedia} className="max-w-full" />
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/40">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-300">Preview</div>
                <button
                  onClick={() => setAutoPreview(prev => !prev)}
                  className={`px-3 py-1 text-xs rounded-md transition-all ${autoPreview
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                >
                  {autoPreview ? 'Stop Preview' : 'Start Preview'}
                </button>
              </div>
              {!selectedQuestion && (
                <div className="text-slate-500 text-sm">Select a question to generate preview.</div>
              )}
              {selectedQuestion && isGenerating && (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating A6 preview...
                </div>
              )}
              {selectedQuestion && !autoPreview && !previewBase64 && !isGenerating && (
                <div className="text-slate-500 text-sm">Preview paused. Start preview to generate.</div>
              )}
              {selectedQuestion && !isGenerating && previewBase64 && (
                <div className="rounded-lg overflow-hidden border border-slate-700 bg-slate-900">
                  <img
                    src={`data:image/png;base64,${previewBase64}`}
                    alt="A6 card preview"
                    className="w-full h-auto"
                  />
                </div>
              )}
              {selectedQuestion && !isGenerating && !previewBase64 && !error && (
                <div className="text-slate-500 text-sm">Preview unavailable.</div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50 rounded-b-xl shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {selectedQuestion ? '1 question selected' : 'No question selected'}
            </p>
            <button
              onClick={handleExport}
              disabled={!selectedQuestion || isGenerating || !previewBase64}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-500 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <Download className="h-5 w-5" />
              Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PngExportModal;
